import { FC, MouseEvent } from "react";
import styled from "styled-components";
import { useQueryClient } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import { FormProvider, useForm, useWatch } from "react-hook-form";

import {
  Actions,
  CloseButton,
  StyledDialog,
} from "@/components/AlertDialogs/Dialog";
import { Modal } from "@/components/Modal";
import Button from "@/components/Button";
import useAlerts from "@/hooks/useAlerts";
import { api } from "@/utils/api";
import Text from "@/components/Text";
import FileField from "@/components/Forms/FileField";
import { handleFormErrors } from "@/utils/helpers";
import { NewDropdown } from "@/components/Forms/Dropdown";
import { RootState } from "@/utils/store";
import { FormLayout, Label, Larger } from "@/components/Forms";
import ValidationMessage from "@/components/Forms/ValidationMessage";
import Checkbox from "@/components/Forms/Checkbox";

type TFormData = {
  file: File | null;
  team_id: string;
  should_retest: boolean;
};

const DEFAULT_VALUES = {
  file: null,
  team_id: "",
  should_retest: true,
};

const Heading = styled(Text)`
  margin-bottom: 16px;
`;

const Dialog = styled(StyledDialog)`
  display: flex;
  flex-direction: column;
`;

const TeamInputHolder = styled.div`
  text-align: left;
`;

interface Props {
  onClose: () => void;
}

const EytvUploadModal: FC<Props> = ({ onClose }) => {
  const alerts = useAlerts();

  const teams = useSelector((state: RootState) => state.auth.user?.teams)?.map(
    (team) => {
      return {
        value: team.id,
        label: team.name,
      };
    }
  );

  const queryClient = useQueryClient();

  const onCloseWithEventPrevention = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    onClose();
  };

  const methods = useForm<TFormData>({
    defaultValues: DEFAULT_VALUES,
  });

  const file = useWatch({ control: methods.control, name: "file" });

  const onSubmit = methods.handleSubmit(
    async ({ file, team_id, should_retest }) => {
      methods.clearErrors();

      const formData = new FormData();

      if (file) {
        formData.append("file", file);
      }

      if (team_id) {
        formData.append("team_id", team_id);
      }

      formData.append("should_retest", should_retest ? "1" : "0");

      try {
        const response = await api.post<TFormData, { error: string }>(
          "/v1/eytv-import",
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          }
        );

        onClose();

        methods.reset(DEFAULT_VALUES);

        if (response.error) {
          return alerts.error(response.error);
        }

        alerts.success("Import successfully started");

        queryClient.invalidateQueries(["eytv_files"], {
          exact: false,
        });
      } catch (error) {
        handleFormErrors<TFormData>(error, methods.setError);
      }
    }
  );

  const { isSubmitting, errors } = methods.formState;

  return (
    <Modal zIndex={50}>
      <Dialog
        initial={{ scale: 0.3 }}
        animate={{ scale: 1 }}
        exit={{ scale: 0.3 }}
      >
        <CloseButton onClick={onCloseWithEventPrevention}>
          <svg
            width="14"
            height="14"
            viewBox="0 0 14 14"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M13 1L1 13"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M13 13L1 1"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </CloseButton>
        <Heading $type="heading2">
          {!file ? "Upload CSV file" : "Import file"}
        </Heading>
        <form onSubmit={onSubmit} autoComplete="off">
          <FormProvider {...methods}>
            <FormLayout>
              <Larger>
                <FileField
                  fileType="other"
                  name="file"
                  notes="CSV"
                  accept=".csv"
                />
              </Larger>
              <Larger>
                <TeamInputHolder>
                  <Label htmlFor="team">Team</Label>
                  <NewDropdown
                    id="team"
                    name="team_id"
                    options={teams ?? []}
                    control={methods.control}
                  />
                  {!!errors.team_id && (
                    <ValidationMessage>
                      {errors.team_id.message}
                    </ValidationMessage>
                  )}
                </TeamInputHolder>
              </Larger>
              <Larger>
                <Checkbox<TFormData>
                  name="should_retest"
                  control={methods.control}
                >
                  Run tests again on already tested videos
                </Checkbox>
              </Larger>
            </FormLayout>
          </FormProvider>
          {!!file && (
            <Actions>
              <Button loading={isSubmitting} disabled={isSubmitting}>
                Import
              </Button>
              <Button onClick={onCloseWithEventPrevention} color="secondary">
                Cancel
              </Button>
            </Actions>
          )}
        </form>
      </Dialog>
    </Modal>
  );
};

export default EytvUploadModal;
